<template>
    <VJoyDrawer
        ref="projectRenewalDrawer"
        class="project-renewal-drawer"
        open
        size="large"
        data-testid="renew-project-drawer"
        @drawer:hide="emit('close:dialog', 'renew')"
    >
        <template #drawer-header>
            <div class="project-renewal-drawer__header">
                {{ t('projectclient.projectview.main.actions.renew.project.title', {0: projectInformation.freelancer.firstName}) }}
            </div>
        </template>
        <template #drawer-subheader>{{ t('projectclient.projectview.main.actions.renew.project.subtitle') }}</template>
        <template #drawer-body>
            <form :id="formId" class="project-renewal-drawer__body">
                <VJoyWrapper direction="column" align="stretch" row-gap="4">
                    <div>
                        <VJoyInput
                            v-model="r$.title.$value"
                            name="project-title"
                            :label="t('projectclient.projectview.main.actions.renew.project.title.label')"
                            :optional-label="$t('generic.form.optional.mention')"
                            :placeholder="projectInformation.contract.subject"
                            data-testid="project-renewal-project-title"
                            :invalid="r$.title.$error"
                        />
                        <VJoyFormError v-if="r$.title.$error" class="joy-mt-1" :errors="r$.title.$errors" />
                    </div>
                    <div>
                        <MaltDatePicker
                            v-model="r$.startDate.$value"
                            name="projectKickoff"
                            data-testid="project-renewal-project-kickoff"
                            :optional-label="$t('generic.form.optional.mention')"
                            :label="t('projectclient.projectview.main.actions.renew.project.kickoff.label')"
                            :invalid="r$.startDate.$error"
                            :min-date="new Date()"
                        />
                        <VJoyFormError v-if="r$.startDate.$error" class="joy-mt-1" :errors="r$.startDate.$errors" />
                    </div>

                    <div v-if="projectInformation.contract.type !== 'FIXED_PRICE'">
                        <VJoyInputNumber
                            v-model="r$.duration.$value"
                            :optional-label="$t('generic.form.optional.mention')"
                            name="project-duration"
                            :label="t('projectclient.projectview.main.actions.renew.project.duration.label')"
                            :placeholder="t('projectclient.projectview.main.actions.renew.project.duration.placeholder')"
                            :unit="t('projectclient.projectview.main.actions.renew.project.duration.unit')"
                            :invalid="r$.duration.$error"
                        />
                        <VJoyFormError v-if="r$.duration.$error" class="joy-mt-1" :errors="r$.duration.$errors" />
                    </div>
                    <VJoyHighlight v-if="projectInformation.lot?.purchaseOrderMandatory" level="info" display-icon>
                        <template #highlight-title>
                            {{ t('projectclient.projectview.main.actions.renew.project.purchase-order-required.highlight.title') }}
                        </template>
                        {{ t('projectclient.projectview.main.actions.renew.project.purchase-order-required.highlight.description') }}
                    </VJoyHighlight>
                    <VJoyWrapper v-if="!isParticipant && hasAuthorityToRenewProject" direction="column" align="stretch" row-gap="4">
                        <VJoyHighlight level="warning" accent>
                            {{ t('projectclient.projectview.main.actions.renew.project.admin.comment.highlight') }}
                        </VJoyHighlight>
                        <div>
                            <VJoyTextarea
                                v-model="r$.comment.$value"
                                :optional-label="r$.comment.$isRequired ? undefined : $t('generic.form.optional.mention')"
                                :label="t('projectclient.projectview.main.actions.renew.project.admin.comment.label')"
                                :placeholder="t('projectclient.projectview.main.actions.renew.project.admin.comment.placeholder')"
                                :invalid="r$.comment.$error"
                            />
                            <VJoyFormError v-if="r$.comment.$error" class="joy-mt-1" :errors="r$.comment.$errors" />
                        </div>
                    </VJoyWrapper>
                </VJoyWrapper>
            </form>
        </template>
        <template #drawer-confirm>
            <VJoySubmit :loading="renewingProject" :action="requestProjectRenewal" size="small" data-testid="renew-project-confirm-button">
                {{ t('generic.form.confirm.label') }}
            </VJoySubmit>
        </template>
    </VJoyDrawer>
</template>

<script setup lang="ts">
    import {ref, computed} from 'vue';
    import {useUserStore} from '@common/stores';
    import {requiredIf, maxLength, dateAfter, isFilled} from '@regle/rules';
    import {storeToRefs} from 'pinia';
    import {MaltDatePicker} from '#components';
    import {VJoySubmit} from '#components';
    import {useTranslation} from '#imports';
    import {useRegle, validateForm} from '@malt/forms';
    import {VJoyDrawer, VJoyFormError, VJoyHighlight, VJoyInput, VJoyInputNumber, VJoyTextarea, VJoyWrapper} from '@maltjoy/core-vue';
    import type {ProjectRenewParam} from '@api';
    import {useProjectInformationStore} from '@/common/stores';

    const emit = defineEmits<{
        'close:dialog': [value: 'renew'];
    }>();

    const {t} = useTranslation();
    const userStore = useUserStore();
    const {roles} = storeToRefs(userStore);

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation, renewingProject} = storeToRefs(projectInformationStore);

    // handle admin validation + clientOpsModal
    const isParticipant = computed(() => projectInformation.value?.participant);
    const hasAuthorityToRenewProject = computed(() => roles.value.includes('ROLE_ADMIN'));

    const formState = ref<ProjectRenewParam>({
        title: projectInformation.value.contract.subject,
        startDate: undefined,
        duration: undefined,
        comment: '',
    });

    const formId = 'renew-project-form';
    const {r$} = useRegle(formState, {
        title: {
            maxLength: maxLength(500),
        },
        startDate: {
            dateAfter: dateAfter(new Date()),
        },
        duration: {
            required: requiredIf(() => projectInformation.value.contract.type !== 'FIXED_PRICE' && isFilled(formState.value.startDate)),
        },
        comment: {
            required: requiredIf(() => !isParticipant.value && hasAuthorityToRenewProject.value),
            maxLength: maxLength(2000),
        },
    });

    async function requestProjectRenewal() {
        const {valid, data} = await validateForm(r$, {formId});
        if (!valid) return;

        await projectInformationStore.renewProject({
            projectId: projectInformation.value.projectId,
            projectRenewParam: data,
        });
        emit('close:dialog', 'renew');
    }
</script>

<style lang="scss" scoped>
    .project-renewal-drawer {
        &__header {
            margin-top: var(--joy-core-spacing-8);
        }

        &__body {
            margin-top: var(--joy-core-spacing-4);
            display: flex;
            flex-direction: column;

            & > label {
                padding-bottom: 0;
            }

            // 1. override to show potential errors closer to the input
            :deep(.joy-input--wrapper) {
                margin-bottom: 0 !important;
            }
        }
    }
</style>
